import { CopyOutlined, DeleteOutlined, PlusOutlined } from '@ant-design/icons';
import {
  App as AntApp,
  Button,
  Form,
  Input,
  Modal,
  Popconfirm,
  Space,
  Switch,
  Table,
  Typography,
} from 'antd';
import { useState } from 'react';
import {
  useConsumers,
  useCreateConsumer,
  useDeleteConsumer,
  useToggleConsumer,
} from '../api/hooks';
import type { Consumer } from '../api/types';
import { extractErrorMessage } from '../api/client';
import { formatDateTime } from '../utils/format';

const { Title, Text, Paragraph } = Typography;

export default function ConsumersPage() {
  const { message } = AntApp.useApp();
  const list = useConsumers();
  const create = useCreateConsumer();
  const toggle = useToggleConsumer();
  const del = useDeleteConsumer();

  const [createOpen, setCreateOpen] = useState(false);
  const [form] = Form.useForm();
  const [newKey, setNewKey] = useState<string | null>(null);

  const onCreate = async () => {
    const v = await form.validateFields();
    try {
      const res = await create.mutateAsync({ name: v.name });
      setCreateOpen(false);
      form.resetFields();
      if (res?.apiKey) {
        setNewKey(res.apiKey);
      } else {
        message.success('Đã tạo khóa API');
      }
    } catch (e) {
      message.error(extractErrorMessage(e));
    }
  };

  const copyKey = async (key: string) => {
    try {
      await navigator.clipboard.writeText(key);
      message.success('Đã sao chép vào clipboard');
    } catch {
      message.warning('Không thể sao chép tự động, vui lòng copy thủ công');
    }
  };

  const columns = [
    { title: 'Tên', dataIndex: 'name', key: 'name' },
    {
      title: 'Khóa API',
      dataIndex: 'apiKey',
      key: 'apiKey',
      render: (v: string) => (v ? <Text code>{v}</Text> : <Text type="secondary">(ẩn)</Text>),
    },
    {
      title: 'Tạo lúc',
      dataIndex: 'createdAt',
      key: 'createdAt',
      render: (v: string) => formatDateTime(v),
    },
    {
      title: 'Bật',
      dataIndex: 'enabled',
      key: 'enabled',
      width: 80,
      render: (enabled: boolean, r: Consumer) => (
        <Switch
          checked={enabled}
          loading={toggle.isPending}
          onChange={(val) => toggle.mutate({ id: r.id, enabled: val })}
        />
      ),
    },
    {
      title: '',
      key: 'actions',
      width: 60,
      render: (_: unknown, r: Consumer) => (
        <Popconfirm
          title="Xóa khóa API này?"
          okText="Xóa"
          cancelText="Hủy"
          onConfirm={() => del.mutate(r.id)}
        >
          <Button size="small" danger icon={<DeleteOutlined />} />
        </Popconfirm>
      ),
    },
  ];

  return (
    <div>
      <Space style={{ width: '100%', justifyContent: 'space-between', marginBottom: 16 }}>
        <Title level={3} style={{ margin: 0 }}>
          Khóa API (Website A)
        </Title>
        <Button type="primary" icon={<PlusOutlined />} onClick={() => setCreateOpen(true)}>
          Tạo khóa API
        </Button>
      </Space>

      <Table<Consumer>
        rowKey="id"
        loading={list.isLoading}
        dataSource={list.data ?? []}
        columns={columns}
      />

      <Modal
        title="Tạo khóa API"
        open={createOpen}
        onCancel={() => setCreateOpen(false)}
        onOk={onCreate}
        confirmLoading={create.isPending}
        okText="Tạo"
        cancelText="Hủy"
      >
        <Form form={form} layout="vertical">
          <Form.Item
            name="name"
            label="Tên đơn vị sử dụng"
            rules={[{ required: true, message: 'Vui lòng nhập tên' }]}
          >
            <Input placeholder="vd: Website A" />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title="Khóa API đã được tạo"
        open={!!newKey}
        onCancel={() => setNewKey(null)}
        footer={[
          <Button key="close" type="primary" onClick={() => setNewKey(null)}>
            Đã lưu, đóng
          </Button>,
        ]}
      >
        <Paragraph type="warning">
          Hãy sao chép và lưu lại khóa này ngay. Khóa sẽ không được hiển thị đầy đủ lần nữa.
        </Paragraph>
        <Space.Compact style={{ width: '100%' }}>
          <Input readOnly value={newKey ?? ''} />
          <Button
            icon={<CopyOutlined />}
            onClick={() => newKey && copyKey(newKey)}
          >
            Sao chép
          </Button>
        </Space.Compact>
      </Modal>
    </div>
  );
}
